import * as React from 'react';
import { Image, View, StyleSheet, Text, Pressable, PixelRatio } from 'react-native';
import * as AppUtilities from "../context/AppUtilities";
import * as AppConstants from "../context/AppConstants";
import * as AppState from "../context/AppState";
import { AppContext } from "../context/AppContext";
import { useSelector, useDispatch } from 'react-redux';

const AD_WIDTH = 320;
const AD_HEIGHT = 50;

const styles = StyleSheet.create({
  adWrapper: {
    //padding: 2,
    backgroundColor: "white",
    borderWidth: 1,
    justifyContent: "center",
  },
  adHeader: {
    fontSize: 10,
    paddingBottom: 2,
    alignSelf: "center",
  },
  adImage: {
    cursor: 'pointer',
    width: AD_WIDTH,
    height: AD_HEIGHT,
  },
  adImage2: {
    width: AD_WIDTH,
    height: AD_HEIGHT,
  },
  pointer: {
    cursor: 'pointer',
  },
  currentViewWrapper: {
    justifyContent: "center",
    width: AD_WIDTH + 2,
    backgroundColor: "white",
    borderWidth: 1,
    //borderBottomWidth: 0,
    padding: 2,
    marginBottom: 2,
  },
  currentViewText: {
    fontSize: 10,
    alignSelf: "center",
  },
});

// Equatorial radius of Earth (in KM)
const R = 6378.137;
const ONE_KM_IN_MILES = 0.621371;

function squared(x) {
  return x * x;
}

function toRad(x) {
  return (x * Math.PI) / 180.0;
}

function hav(x) {
  return squared(Math.sin(x / 2));
}

function haversineDistanceMiles(aLatitude, aLongitude, bLatitude, bLongitude) {
  const aLat = toRad(aLatitude);
  const bLat = toRad(bLatitude);
  const aLng = toRad(aLongitude);
  const bLng = toRad(bLongitude);

  const ht = hav(bLat - aLat) + Math.cos(aLat) * Math.cos(bLat) * hav(bLng - aLng);
  const distanceKm = 2 * R * Math.asin(Math.sqrt(ht));
  return distanceKm * ONE_KM_IN_MILES;
}

function isAdVisible(ad, viewportCenterLatitude, viewportCenterLongitude) {
  let result = false;
  if (!ad.geofence_center_latitude) {
    // If there's no geofencing, then the ad is visible by default
    result = true;
  } else {
    if (viewportCenterLatitude) {
      const distanceMiles = haversineDistanceMiles(
        ad.geofence_center_latitude,
        ad.geofence_center_longitude,
        viewportCenterLatitude,
        viewportCenterLongitude
      );
      if (distanceMiles < ad.geofence_distance) {
        result = true;
      }
      /*
      console.log({
        aLat: ad.geofence_center_latitude,
        aLng: ad.geofence_center_longitude,
        bLat: viewportCenterLatitude,
        bLng: viewportCenterLongitude,
        distanceMiles: distanceMiles,
        result: result,
      });
      */
    }
  }
  return result;
}

function getFirstGeofencedAd(ads, mapCenter) {
  const viewportCenterLatitude = mapCenter ? mapCenter.lat : null;
  const viewportCenterLongitude = mapCenter ? mapCenter.lng : null;
  for (let i = 0; i < ads.length; i++) {
    const ad = ads[i];
    if (ad.geofence_center_latitude &&
        isAdVisible(ad, viewportCenterLatitude, viewportCenterLongitude))
    {
      return i;
    }
  }
  return null;
}

function getNextAdIndex(ads, currentIndex, mapCenter) {
  let result = null;
  const viewportCenterLatitude = mapCenter ? mapCenter.lat : null;
  const viewportCenterLongitude = mapCenter ? mapCenter.lng : null;

  for (let i = currentIndex + 1; i < ads.length; i++) {
    if (isAdVisible(ads[i], viewportCenterLatitude, viewportCenterLongitude)) {
      result = i;
      break;
    }
  }

  if (!result) {
    // Nothing to the end of the list, so now start from the beginning up until
    // where we were
    for (let j = 0; j <= currentIndex; j++) {
      if (isAdVisible(ads[j], viewportCenterLatitude, viewportCenterLongitude)) {
        result = j;
        break;
      }
    }
  }

  return result;
}

export function MapAds({ navigation }) {
  const ads = useSelector((state) => state.ads);
  const adRefreshInterval = useSelector((state) => state.adRefreshInterval);
  const hideAds = useSelector((state) => state.hideAds);
  const lightMode = useSelector((state) => state.lightMode);
  const [adIndex, setAdIndex] = React.useState(-1);
  const dispatch = useDispatch();
  const mapCenter = useSelector((state) => state.mapCenter);

  React.useEffect(() => {
    const id = setTimeout(
      () => {
        if (ads && ads.length > 0) {
          const newIndex = getNextAdIndex(ads, adIndex, mapCenter);
          //console.log("Setting adIndex " + newIndex + " from timer");
          setAdIndex(newIndex);
        }
      },
      adRefreshInterval
    );
    return () => {
      clearTimeout(id);
    };
  }, [adIndex, mapCenter]);

  React.useEffect(() => {
    if (ads && ads.length > 0) {

      if (AppUtilities.DEBUGLEVEL.ads) {
        AppUtilities.debug("MapAds.useEffect(ads) with mapCenter: " + AppUtilities.toJSONPretty(mapCenter));
        AppUtilities.dir(ads);
      }

      // NOTE: This function only calculates the starting index any
      // time the ads list or map center change. Ad rolling happens in a subsequent useEffect.
        
      // If there's an ad geofenced to our mapCenter, then
      // explicitly start there first
      let newIndex = null;
      if (mapCenter) {
        newIndex = getFirstGeofencedAd(ads, mapCenter);
        if (AppUtilities.DEBUGLEVEL.ads) {
          AppUtilities.debug("MapAds.useEffect(ads) getFirstGeofencedAd returned: " + newIndex);
        }
      }

      if (AppUtilities.isNullOrUndefined(newIndex)) {
        // Otherwise, start somewhere random
        const randomStart = AppUtilities.randomInt(0, ads.length - 1);
        newIndex = getNextAdIndex(ads, randomStart, mapCenter);

        if (AppUtilities.DEBUGLEVEL.ads) {
          AppUtilities.debug("MapAds.useEffect(ads) non-geofenced index: " + newIndex);
        }
      }

      setAdIndex(newIndex);
    }
  }, [ads, mapCenter]);

  if (hideAds || lightMode || !ads || ads.length == 0 || adIndex >= ads.length || adIndex < 0) {
    return null;
  }

  const clickAd = () => {
    const ad = ads[adIndex];
    //AppUtilities.debug("clickAd: " + AppUtilities.toJSONPretty(ad));
    if (ad.delivery) {
      dispatch(AppState.createDispatch(
        AppConstants.DISPATCH.SET_DELIVERY_LOCATION_FILTER,
        ad.name,
      ));

      navigation.navigate(AppConstants.SCREENS.DELIVERY_STACK);
    } else {
      // The link already has UTM on it
      AppUtilities.openLink(ad.link);
    }
  };

  if (AppUtilities.DEBUGLEVEL.ads) {
    AppUtilities.debug("MapAds.render index: " + adIndex + ", ad: " + AppUtilities.toJSONPretty(ads[adIndex]));
  }

  let imagesrc = ads[adIndex].img;
  if (ads[adIndex].srcset && ads[adIndex].srcset.length > 0) {
    const srcset = ads[adIndex].srcset.replaceAll(",https://", ", https://");
    imagesrc = AppUtilities.selectImageFromSrcSet(srcset);
  }

  return (
    <View>
      {/*<View style={styles.currentViewWrapper}>
        <Text
          style={styles.currentViewText}
        >
          Viewing: {AppUtilities.createInternalLink(navigation, shortDescription, shortDescriptionLink)}
        </Text>
      </View>*/}
      <View style={styles.adWrapper}>
        {/*
        <Text
          style={styles.adHeader}
        >
          Can't find something local? Check out humane delivery options:
        </Text>
        */}

        <Pressable onPress={clickAd} style={styles.adImage}>
          {
            AppUtilities.isWeb() ?
                <img key={ads[adIndex].name} src={ads[adIndex].img} srcSet={ads[adIndex].srcset ? ads[adIndex].srcset : ""} width={AD_WIDTH} height={AD_HEIGHT} style={styles.pointer} />
              :
                <Image
                  key={ads[adIndex].name}
                  source={{ uri: imagesrc }}
                  style={styles.adImage2}
                  width={AD_WIDTH}
                  height={AD_HEIGHT}
                  onError={(error) => AppUtilities.handleException(error, "MapAds render Image")}
                />
          }
        </Pressable>
      </View>
    </View>
  );
}
